import { Chip, InputWrapper, Loader, MantineSize, MantineSpacing, ScrollArea, Text, rem } from '@mantine/core';
import cn from 'classnames';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import AbsoluteError from 'components/ui/AbsoluteError';
import { FieldSelectMultiStore } from 'stores/local/shared/form/FieldSelectStore';
import { useDevices } from 'utils/devices';
import { isDefined } from 'utils/isDefined';

import s from './FieldChipGroup.module.scss';

type FieldChipGroupProps = {
  className?: string;
  label?: string;
  labelClassName?: string;
  size?: MantineSize;
  gap?: MantineSpacing;
  scrollable?: boolean;
  single?: boolean;
  requireOne?: boolean;
  store: FieldSelectMultiStore;
  groupClassName?: string;
  loaderClassName?: string;
  disabled?: boolean;
  emptyOptionsText?: string;
};

const FieldChipGroup: React.FC<FieldChipGroupProps> = ({
  className,
  store,
  label,
  labelClassName,
  size,
  groupClassName,
  loaderClassName,
  gap = 'sm',
  scrollable = false,
  single,
  requireOne = false,
  disabled,
  emptyOptionsText = 'Нет доступных слотов',
}) => {
  const { isPhone } = useDevices();

  const onChange = React.useCallback(
    (keys: string[]) => {
      const normalizedKeys = single ? keys.slice(-1) : keys;

      if (requireOne && normalizedKeys.length === 0) {
        return;
      }

      runInAction(() => {
        const items = normalizedKeys.map((k) => store.optionsMap[k]).filter(isDefined);

        store.setValue(items);
      });
    },
    [store, single, requireOne],
  );

  const sizeProp = size ? size : isPhone ? 'md' : 'lg';

  if (!store.shown) {
    return null;
  }

  const chips = (
    <div className={cn(s.root__group, scrollable && s.root__group_scrollable, groupClassName)} style={{ gap }}>
      <Chip.Group multiple onChange={onChange} value={store.valueKeys}>
        {store.meta.isLoading && <Loader className={loaderClassName} />}
        {store.options.length == 0 && !store.meta.isLoading && <Text size="md">{emptyOptionsText}</Text>}
        {store.options.length !== 0 &&
          !store.meta.isLoading &&
          store.options.map((item) => (
            <Chip
              size={sizeProp}
              key={item.key}
              value={item.key}
              disabled={disabled}
              classNames={{ label: cn(s.root__label, labelClassName), iconWrapper: s.root__icon }}
            >
              {item.value}
            </Chip>
          ))}
      </Chip.Group>
    </div>
  );

  return (
    <InputWrapper
      withAsterisk={store.required}
      className={className}
      size={sizeProp}
      label={label}
      labelProps={{
        mb: rem(8),
      }}
      error={store.hasError && <AbsoluteError error={store.error} />}
    >
      {scrollable ? (
        <ScrollArea type="never" scrollbars="x" className={s.root__scrollArea}>
          {chips}
        </ScrollArea>
      ) : (
        chips
      )}
    </InputWrapper>
  );
};

export default observer(FieldChipGroup);
